import React, { useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import Heading from 'components/Luxkit/Typography/Heading'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'
import LineArrowsRightDownIcon from 'components/Luxkit/Icons/line/LineArrowsRightDownIcon'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import ModalContext from 'contexts/ModalContext'
import LoyaltyPoints from 'loyalty/components/LoyaltyPoints'
import { OptimizelyExperiments } from 'constants/optimizely'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { isAvailableForPurchase, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import MobileMultipleRoomsChangeRoomModal from './Modals/MobileMultipleRoomsChangeRoomModal'
import MobileMultipleRoomsRoomOptionPriceDetails from './MobileMultipleRoomsRoomOptionPriceDetails'
import MultipleRoomsInclusions from '../MultipleRoomsInclusions'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineEditAltIcon from 'components/Luxkit/Icons/line/LineEditAltIcon'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import { pluralizeToString } from 'lib/string/pluralize'
import { getAvailablePackagesByRoom, PackagesOptionsViewsByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import config from 'constants/config'
import { unique } from 'lib/array/arrayUtils'

const RoomContainer = styled(Pane)`
  padding: ${rem(20)};
`

interface Props {
  offer: App.HotelOffer;
  checkIn: string;
  room: App.RoomOccupants;
  roomIndex: number;
  availableRoomCount?: number;
  isFlightsSelected: boolean;
  availableForPurchase: boolean;
  packageView: OfferPackageOptionView;
  availablePackagesByRoom: PackagesOptionsViewsByRoom;
}

function MobileMultipleRoomsIndividualRoomOption(props: Props) {
  const { offer, checkIn, room, roomIndex, availableRoomCount, availableForPurchase, isFlightsSelected, packageView, availablePackagesByRoom } = props

  const showModal = useContext(ModalContext)
  const dispatch = useOfferPageDispatch()

  const firstRoomImageId = packageView.package.roomType?.images?.[0]?.id
  const availablePackagesCount = availablePackagesByRoom[room.roomId].length

  const availablePackageRoomTypeCount = useMemo(() => {
    return unique(
      Object.values(availablePackagesByRoom)
        .flat()
        .map(pkg => pkg.package.roomType.id),
    ).length
  }, [availablePackagesByRoom])

  const croPopularOptionBookingRemaining = !!useOptimizelyExperiment(OptimizelyExperiments.croPopularOptionBookingRemaining, !!availableRoomCount && !!checkIn)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, { packageView, skip: !isFlightsSelected }),
  ], [offer, packageView, isFlightsSelected])

  const onOpenRoomsModal = useCallback(async() => {
    const packageOption = await showModal<App.HotelPackage | undefined>(
      <MobileMultipleRoomsChangeRoomModal room={room} roomIndex={roomIndex} selectedPackageView={packageView} />,
    )

    if (packageOption) {
      dispatch({
        type: OfferPageActions.selectRoomPackage,
        packageOption,
        room,
      })
    }
  }, [dispatch, room, roomIndex, showModal, packageView])

  return (
    <RoomContainer>
      <Group direction="vertical" gap={16}>
        <Group direction="vertical" gap={8}>
          <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
            <Group direction="vertical" gap={12}>
              <StructureLabel variant="dark">Room {roomIndex + 1}</StructureLabel>
              <div>
                <Heading variant="heading4">{packageView.package.roomType?.name}</Heading>
                <BodyText variant="medium" colour="neutral-three">{formatOccupantsShort(room)}</BodyText>
              </div>
            </Group>
            <Image id={firstRoomImageId} width={80} height={80} dpr={3} />
          </Group>
          <LabelGroup>
            {checkIn && !croPopularOptionBookingRemaining && <RoomsRemainingLabel roomCount={packageView.availableRooms ?? 0} threshold="rate" />}
            {checkIn && croPopularOptionBookingRemaining && availableRoomCount && <OfferUrgencyRoomCount availableRoomCount={availableRoomCount}/> }
            <BookingsCountLabel bookings={packageView.package.roomType?.stats.booked ?? 0} threshold="room" />
          </LabelGroup>
          <LabelGroup>
            {!!packageView.package.roomType?.sizeSqm && <StructureLabel variant="light" startIcon={<LineArrowsRightDownIcon />}>
              {packageView.package.roomType.sizeSqm} m&sup2;
            </StructureLabel>}
            {!!packageView.package.roomType?.numberOfBedrooms && <StructureLabel variant="light" startIcon={<LineBedIcon />}>
              {packageView.package.roomType.numberOfBedrooms} bedrooms
            </StructureLabel>}
          </LabelGroup>
          {availableForPurchase && <FlashCancellationPolicies
            checkInDate={checkIn}
            propertyTimezoneOffset={offer.property?.timezoneOffset}
            cancellationPolicy={packageView.package.roomRate?.cancellationPolicy}
          />}
          <LoyaltyPoints
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
          />
          <MultipleRoomsInclusions offer={offer} packageOption={packageView} />
        </Group>
        <MobileMultipleRoomsRoomOptionPriceDetails room={room} packageOption={packageView}>
          {config.PACKAGE_CHANGE_UPGRADE_ENABLED && (
            <Group direction="vertical" gap={8}>
              <TextButton
                variant="dark"
                kind="secondary"
                fit="flex"
                size="medium"
                startIcon={<LineEditAltIcon />}
                onClick={onOpenRoomsModal}
                disabled={availablePackagesCount <= 1}
              >
                Change room
              </TextButton>
              <Group direction="horizontal" horizontalAlign="center">
                <PriceRowCaption>
                  {availablePackagesCount <= 1 && 'No additional options'}
                  {availablePackageRoomTypeCount > 1 && <>{pluralizeToString('room option', availablePackageRoomTypeCount)} available</>}
                </PriceRowCaption>
              </Group>
            </Group>
          )}
        </MobileMultipleRoomsRoomOptionPriceDetails>
      </Group>
    </RoomContainer>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer!,
  rooms: state.rooms,
  checkIn: state.checkInDate,
  isFlightsSelected: state.flights,
  availableForPurchase: isAvailableForPurchase(state, appState),
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
}), MobileMultipleRoomsIndividualRoomOption)
